import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { LogOut, RefreshCw, TrendingUp, FileText, BarChart3, Link2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const pct = (n) => `${(n * 100).toFixed(2)} %`;

const GoogleButton = ({ onClick }) => (
  <Button
    data-testid="google-login-button"
    onClick={onClick}
    className="flex w-full items-center justify-center gap-3 rounded-full bg-white py-6 text-base font-medium text-gray-800 hover:bg-gray-100"
  >
    <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.7-.4-3.9z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C41 35.4 44 30.2 44 24c0-1.3-.1-2.7-.4-3.9z" />
    </svg>
    Se connecter avec Google
  </Button>
);

const LoginCard = ({ error }) => {
  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <div className="flex min-h-[70vh] items-center justify-center px-4 py-24">
      <Card data-testid="admin-login-card" className="w-full max-w-md border-[#2a2a2a] bg-[#141414]">
        <CardHeader className="items-center text-center">
          <img src="/logo-512.png" alt="Resineko" className="mb-4 h-20 w-20 rounded-full" />
          <CardTitle className="font-serif text-2xl text-[#F4F4F0]">Espace administrateur</CardTitle>
          <p className="text-sm text-[#8C8C8C]">Réservé à Resineko — accès par compte Google autorisé</p>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <p data-testid="admin-login-error" className="rounded-md border border-red-900 bg-red-950/40 px-4 py-3 text-sm text-red-300">
              {error}
            </p>
          )}
          <GoogleButton onClick={login} />
        </CardContent>
      </Card>
    </div>
  );
};

const MetricCard = ({ label, value, testid }) => (
  <Card className="border-[#2a2a2a] bg-[#141414]">
    <CardHeader className="pb-2">
      <CardTitle className="text-xs font-medium uppercase tracking-widest text-[#8C8C8C]">{label}</CardTitle>
    </CardHeader>
    <CardContent>
      <p data-testid={testid} className="text-3xl font-semibold text-[#F4F4F0]">{value}</p>
    </CardContent>
  </Card>
);

const GscTable = ({ title, rows, testid }) => (
  <Card className="border-[#2a2a2a] bg-[#141414]">
    <CardHeader>
      <CardTitle className="text-lg text-[#F4F4F0]">{title}</CardTitle>
    </CardHeader>
    <CardContent>
      <Table data-testid={testid}>
        <TableHeader>
          <TableRow className="border-[#2a2a2a]">
            <TableHead className="text-[#8C8C8C]">Élément</TableHead>
            <TableHead className="text-right text-[#8C8C8C]">Clics</TableHead>
            <TableHead className="text-right text-[#8C8C8C]">Impressions</TableHead>
            <TableHead className="text-right text-[#8C8C8C]">CTR</TableHead>
            <TableHead className="text-right text-[#8C8C8C]">Position</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={i} className="border-[#2a2a2a]">
              <TableCell className="max-w-md break-all text-[#F4F4F0]">{r.keys?.[0]}</TableCell>
              <TableCell className="text-right text-[#F4F4F0]">{r.clicks}</TableCell>
              <TableCell className="text-right text-[#F4F4F0]">{r.impressions}</TableCell>
              <TableCell className="text-right text-[#F4F4F0]">{pct(r.ctr)}</TableCell>
              <TableCell className="text-right text-[#F4F4F0]">{r.position.toFixed(1)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </CardContent>
  </Card>
);

const GscPanel = () => {
  const [status, setStatus] = useState(null);
  const [perf, setPerf] = useState(null);
  const [error, setError] = useState("");

  const loadStatus = async () => {
    const r = await fetch(`${API}/api/admin/gsc/status`, { credentials: "include" });
    if (!r.ok) throw new Error("status");
    const s = await r.json();
    setStatus(s);
    return s;
  };

  const loadPerf = async () => {
    const r = await fetch(`${API}/api/admin/gsc/performance`, { credentials: "include" });
    if (!r.ok) {
      const body = await r.json().catch(() => ({}));
      if (body.detail === "Autorisation Google expirée — reconnectez Search Console") {
        setStatus((s) => ({ ...s, connected: false }));
        return;
      }
      throw new Error(body.detail || "perf");
    }
    setPerf(await r.json());
  };

  const load = async () => {
    setError("");
    try {
      const s = await loadStatus();
      if (s.configured && s.connected) await loadPerf();
    } catch {
      setError("Impossible de charger les données Search Console pour le moment.");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    const r = await fetch(`${API}/api/admin/gsc/connect`, { credentials: "include" });
    const body = await r.json();
    if (body.url) window.location.href = body.url;
  };

  if (error) return <p data-testid="gsc-error" className="text-sm text-red-300">{error}</p>;
  if (!status) return <p className="text-sm text-[#8C8C8C]">Chargement…</p>;

  if (!status.configured) {
    return (
      <div data-testid="gsc-not-configured" className="rounded-lg border border-amber-900/60 bg-amber-950/30 p-5 text-sm leading-relaxed text-amber-200">
        La connexion automatique à Search Console nécessite une petite configuration Google Cloud (environ 10 minutes, guidée).
        Demandez-moi le guide pas à pas dans le chat et je vous accompagne écran par écran.
      </div>
    );
  }

  if (!status.connected) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-[#8C8C8C]">
          Cliquez ci-dessous pour autoriser la lecture de vos données Search Console (propriété {status.property}).
        </p>
        <Button data-testid="gsc-connect-button" onClick={connect} className="rounded-full bg-[#047857] text-white hover:bg-[#059669]">
          <Link2 className="mr-2 h-4 w-4" /> Connecter Google Search Console
        </Button>
      </div>
    );
  }

  if (!perf) return <p className="text-sm text-[#8C8C8C]">Chargement des performances…</p>;

  return (
    <div className="space-y-6">
      <p className="text-sm text-[#8C8C8C]">
        {perf.property} — du {perf.startDate} au {perf.endDate}
      </p>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard label="Clics" value={perf.summary.clicks} testid="gsc-clicks" />
        <MetricCard label="Impressions" value={perf.summary.impressions} testid="gsc-impressions" />
        <MetricCard label="CTR moyen" value={pct(perf.summary.ctr)} testid="gsc-ctr" />
        <MetricCard label="Position moyenne" value={perf.summary.position.toFixed(1)} testid="gsc-position" />
      </div>
      <GscTable title="Requêtes les plus fréquentes" rows={perf.topQueries} testid="gsc-queries-table" />
      <GscTable title="Pages les plus vues" rows={perf.topPages} testid="gsc-pages-table" />
    </div>
  );
};

const DevisPanel = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    setError("");
    try {
      const r = await fetch(`${API}/api/admin/devis`, { credentials: "include" });
      if (!r.ok) throw new Error();
      setData(await r.json());
    } catch {
      setError("Impossible de charger les demandes de devis.");
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (error) return <p data-testid="devis-error" className="text-sm text-red-300">{error}</p>;
  if (!data) return <p className="text-sm text-[#8C8C8C]">Chargement…</p>;

  return (
    <Card className="border-[#2a2a2a] bg-[#141414]">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg text-[#F4F4F0]">
          Demandes reçues <Badge className="ml-2 bg-[#047857] text-white" data-testid="devis-total">{data.total}</Badge>
        </CardTitle>
        <Button data-testid="devis-refresh-button" variant="outline" size="sm" onClick={load} className="border-[#2a2a2a] text-[#8C8C8C] hover:text-white">
          <RefreshCw className="mr-2 h-4 w-4" /> Actualiser
        </Button>
      </CardHeader>
      <CardContent>
        {data.devis.length === 0 ? (
          <p className="text-sm text-[#8C8C8C]">Aucune demande pour le moment.</p>
        ) : (
          <Table data-testid="devis-table">
            <TableHeader>
              <TableRow className="border-[#2a2a2a]">
                <TableHead className="text-[#8C8C8C]">Date</TableHead>
                <TableHead className="text-[#8C8C8C]">Client</TableHead>
                <TableHead className="text-[#8C8C8C]">Contact</TableHead>
                <TableHead className="text-[#8C8C8C]">Ville</TableHead>
                <TableHead className="text-[#8C8C8C]">Projet</TableHead>
                <TableHead className="text-right text-[#8C8C8C]">Photos</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.devis.map((d) => (
                <TableRow key={d.id} className="border-[#2a2a2a]">
                  <TableCell className="whitespace-nowrap text-[#8C8C8C]">{fmtDate(d.created_at)}</TableCell>
                  <TableCell className="text-[#F4F4F0]">{d.prenom} {d.nom}</TableCell>
                  <TableCell className="text-[#F4F4F0]">
                    <a href={`tel:${d.telephone}`} className="text-[#2FBF8F] hover:underline">{d.telephone}</a>
                    <br />
                    <a href={`mailto:${d.email}`} className="text-xs text-[#8C8C8C] hover:underline">{d.email}</a>
                  </TableCell>
                  <TableCell className="text-[#F4F4F0]">{d.ville || "—"}</TableCell>
                  <TableCell className="max-w-xs text-[#F4F4F0]">
                    {[d.revetement, d.surface && `${d.surface}`, d.delai].filter(Boolean).join(" · ") || "—"}
                  </TableCell>
                  <TableCell className="text-right text-[#F4F4F0]">
                    {(d.photos || []).map((p, i) => (
                      <a key={p} href={`${API}/api/devis-photos/${p}`} target="_blank" rel="noreferrer" className="mr-2 text-[#2FBF8F] hover:underline">
                        {i + 1}
                      </a>
                    ))}
                    {(d.photos || []).length === 0 && "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

export default function Admin() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(!location.state?.user);
  const [loginError, setLoginError] = useState("");
  const processed = useRef(false);

  // Retour de la connexion Google : échange du session_id (une seule fois)
  const isCallback = location.hash?.includes("session_id=");
  useEffect(() => {
    if (!isCallback || processed.current) return;
    processed.current = true;
    const sessionId = new URLSearchParams(location.hash.slice(1)).get("session_id");
    (async () => {
      try {
        const r = await fetch(`${API}/api/auth/session`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ session_id: sessionId }),
        });
        if (r.status === 403) {
          setLoginError("Accès refusé : seul le compte Google de l'administrateur est autorisé.");
          setChecking(false);
          navigate("/admin", { replace: true });
          return;
        }
        if (!r.ok) throw new Error();
        const u = await r.json();
        navigate("/admin", { replace: true, state: { user: u } });
        setUser(u);
        setChecking(false);
      } catch {
        setLoginError("La connexion a échoué. Réessayez.");
        setChecking(false);
        navigate("/admin", { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCallback]);

  // Vérification de session existante (le serveur fait foi)
  useEffect(() => {
    if (isCallback || user) return;
    (async () => {
      try {
        const r = await fetch(`${API}/api/auth/me`, { credentials: "include" });
        if (!r.ok) throw new Error();
        setUser(await r.json());
      } catch {
        setUser(null);
      } finally {
        setChecking(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (new URLSearchParams(location.search).get("gsc") === "connected") {
      toast.success("Search Console connecté — vos données se chargent.");
    }
  }, [location.search]);

  const logout = async () => {
    await fetch(`${API}/api/auth/logout`, { method: "POST", credentials: "include" });
    setUser(null);
  };

  return (
    <div data-testid="admin-page" className="bg-[#0A0A0A] text-[#F4F4F0]">
      <Seo title="Espace administrateur — Resineko" description="Espace privé Resineko." path="/admin" noindex />
      {checking ? (
        <div className="flex min-h-[60vh] items-center justify-center">
          <p data-testid="admin-loading" className="text-[#8C8C8C]">Chargement…</p>
        </div>
      ) : !user ? (
        <LoginCard error={loginError} />
      ) : (
        <div className="mx-auto max-w-6xl space-y-10 px-4 py-16">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              {user.picture && <img src={user.picture} alt="" className="h-12 w-12 rounded-full" referrerPolicy="no-referrer" />}
              <div>
                <h1 className="font-serif text-2xl sm:text-3xl">Tableau de bord</h1>
                <p data-testid="admin-user-email" className="text-sm text-[#8C8C8C]">{user.name} — {user.email}</p>
              </div>
            </div>
            <Button data-testid="admin-logout-button" variant="outline" onClick={logout} className="border-[#2a2a2a] text-[#8C8C8C] hover:text-white">
              <LogOut className="mr-2 h-4 w-4" /> Déconnexion
            </Button>
          </div>

          <section className="space-y-4">
            <h2 className="flex items-center gap-2 text-lg font-medium text-[#2FBF8F]">
              <FileText className="h-5 w-5" /> Demandes de devis
            </h2>
            <DevisPanel />
          </section>

          <section className="space-y-4">
            <h2 className="flex items-center gap-2 text-lg font-medium text-[#2FBF8F]">
              <BarChart3 className="h-5 w-5" /> Search Console — 28 derniers jours
            </h2>
            <GscPanel />
          </section>

          <section className="rounded-lg border border-[#2a2a2a] p-5 text-sm text-[#8C8C8C]">
            <h2 className="mb-2 flex items-center gap-2 text-lg font-medium text-[#2FBF8F]">
              <TrendingUp className="h-5 w-5" /> Suivi SEO
            </h2>
            Revenez me voir dans le chat avec cette page ouverte (ou une capture) : j'analyse vos requêtes, vos pages et vos positions,
            puis je vous propose les prochaines actions de référencement — comme une agence, avec vos vraies données.
          </section>
        </div>
      )}
    </div>
  );
}
